/** Shared API shapes — mirror backend/app/schemas (kept small on purpose). */

export interface Me {
  id: string;
  email: string;
  full_name: string;
  role: string;
  phone?: string | null;
  tenant_id: string | null;
  /** Tenant branding for the white-label header. Null for super-admins —
   *  the SPA then shows the platform name (APP_NAME). */
  tenant_name?: string | null;
  tenant_logo_url?: string | null;
  is_active: boolean;
  is_verified: boolean;
  language_preference: string;
  notification_email_optout: boolean;
  /** Self-serve onboarding state. `onboarded` is false for staff accounts
   *  too — the SPA gates the wizard redirect on `is_self_serve` so only solo
   *  learners are routed into it. */
  onboarded: boolean;
  onboarding: OnboardingAnswers;
  is_self_serve: boolean;
  /** A self-serve account with a single seat — Team/Access are hidden for
   *  these until they buy more seats. */
  is_solo: boolean;
  /** False until the learner finishes or skips the guided in-app tour. */
  tour_completed: boolean;
  /** Whether the account has two-factor auth enabled. */
  totp_enabled?: boolean;
}

/** Answers a self-serve learner gives in the onboarding wizard. All optional
 *  until the wizard is built out (S1); the shape is shared with the
 *  recommendation engine. */
export interface OnboardingAnswers {
  goal?: string;
  level?: string;
  board?: string;
  target_level?: string;
  timeline?: string;
}

export interface TeamMember {
  id: string;
  email: string;
  full_name: string;
  role: string;
  phone: string | null;
  is_active: boolean;
  is_verified: boolean;
  language_preference: string;
  notification_email_optout: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface Capabilities {
  read: string[];
  write: string[];
}

export interface Override {
  domain: string;
  action: "read" | "write";
  granted: boolean;
}

export interface Tenant {
  id: string;
  legal_name: string;
  display_name: string | null;
  subdomain: string;
  subscription_tier: string;
  is_active: boolean;
  is_demo: boolean;
  max_users: number | null;
  max_branches: number | null;
  country: string;
  last_active_at: string | null;
  created_at: string;
}

export interface ProvisionResult {
  tenant: Tenant;
  admin_email: string;
  invite_link: string;
  enabled_modules: string[];
}

export interface ModuleStatus {
  code: string;
  name: string;
  category: string;
  description: string;
  enabled: boolean;
  always_on: boolean;
  enabled_at: string | null;
  expires_at: string | null;
}

export interface SeatPreview {
  limit: number | null;
  active_users: number;
  kept_count: number;
  would_deactivate: number;
}

export interface AppNotification {
  id: string;
  kind: string;
  severity: "info" | "warning" | "critical" | "success";
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

export interface InvitePreview {
  email: string;
  full_name: string;
  school: string | null;
  role: string;
}

export const TIERS = ["trial", "starter", "growth", "scale"] as const;
export type Tier = (typeof TIERS)[number];

/** Roles a tenant admin can assign (super_admin is platform-only).
 * Keep in sync with backend ALLOWED_ROLES minus super_admin. */
export const ASSIGNABLE_ROLES = [
  "tenant_admin",
  "academic_head",
  "teacher",
  "office",
  "accountant",
  "viewer",
  "learner",
] as const;
